import { ProjectService } from './../../../project.service';
import { Job, JobExtensionInfo, JobPhoto } from './../job.model';
import { AuthService } from './../../../../auth/auth.service';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '../../../../core/notification/notification.service';
import { DbService } from '../../../../core/db/db.service';
import { JobLinkingsComponent } from './job-linkings/job-linkings.component';
import { JobExtension } from './job-extension/job-extension.model';
import { catchError, debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Article, ArticlePricing, DuplicatePhotoSetting } from '../../../../service-groups/articles/article.model';
import { JobExtensionComponent } from './job-extension/job-extension.component';
import { Project } from '../../../project.model';
import {
    DataCollapseSetting,
    DataRecordingSetting,
    ServiceGroup,
    ServiceGroupPhotoCategory,
    SignatureLockSetting,
} from '../../../../service-groups/service-group.model';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { BuildingPlan, PlanStatus } from '../../project-building-plans/building-plan.model';
import { forkJoin, lastValueFrom, of, ReplaySubject, Subscription } from 'rxjs';
import { UtilsService } from '../../../../core/utils/utils.service';
import { LoggerService } from '../../../../core/logger/logger.service';
import { ModalController } from '@ionic/angular';
import { SettingsService } from '../../../../settings/settings.service';
import { Attachment } from '../../../../core/attachment/attachment.model';
import { AttachmentSelectComponent } from '../../../../core/attachment/attachment-select/attachment-select.component';
import { DpNotificationType } from '../../../../core/shared-models/notification.model';
import moment from 'moment/moment';
import { Field, FieldType, PossibleFieldValue } from '../../../../service-groups/articles/article-details/field/field.model';
import { JobAdditionalFieldsComponent } from './job-additional-fields/job-additional-fields.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../../../core/shared-models/user.model';
import { Product } from '../../../../products/product.model';
import { StorageService } from '../../../../core/storage/storage.service';
import { DueDate } from '../../../../calendar/due-date.model';

export enum JobModalDismissState {
    Close = 1,
    Save = 2,
    Delete = 3,
}

export interface JobModalResult {
    state: JobModalDismissState;
    data?: any;
}

@Component({
    selector: 'app-job-details',
    templateUrl: './job-details.component.html',
    styleUrls: ['./job-details.component.scss'],
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobDetailsComponent implements OnInit, OnDestroy, OnChanges {
    @Input() job: Job;
    @Input() project: Project;

    @Input() set serviceGroup(sg: ServiceGroup) {
        this._serviceGroup = sg;

        this.updateSegmentVisibility();
        this.prepareArticles();
    }

    get serviceGroup() {
        return this._serviceGroup;
    }

    get canOpenDueDates() {
        return this.settingsSrv.getUserRights().canSeeCalendar !== 'none';
    }

    private _serviceGroup: ServiceGroup;

    @Input() asDialog = false;
    @Input() subView = false;
    @Input() taskId: string = null;

    @ViewChild('contentScrollContainer') scrollContainer?: ElementRef;
    @ViewChild('jobAdditionalFieldsComponent') jobAdditionalFieldsComponent?: JobAdditionalFieldsComponent;

    altPressed = false;
    // alt reorder logic
    @HostListener('window:keydown.alt') onAltDown() {
        this.altPressed = true;
    }

    @HostListener('window:keyup.alt') onAltUp() {
        this.altPressed = false;
    }

    shiftPressed = false;

    @HostListener('window:keydown.shift', ['$event']) onShiftDown(e: KeyboardEvent) {
        if ((e.target as HTMLElement).closest('input, textarea, [contenteditable]')) return;
        this.shiftPressed = true;
        this.onToggleReorder();
    }

    @HostListener('window:keyup.shift', ['$event']) onShiftUp(e: KeyboardEvent) {
        if ((e.target as HTMLElement).closest('input, textarea, [contenteditable]')) return;
        this.shiftPressed = false;
        this.onToggleReorder();
    }

    changesAllowed = false;
    possibleWorkers: Array<User> = [];
    currentUserID = undefined;

    jobFormGroup: UntypedFormGroup; // the job as a formGroup (initialized during ngOnInit)

    buildingPlanList: Array<BuildingPlan>; // currently available building plans
    jobBuildingPlan: BuildingPlan; // the building plan on which the job is currently marked

    availableArticles: Array<Article> = []; // list of articles which are allowed to be added by the current license type
    filteredAvailableArticles: Array<Article> = []; // used for filtering availableArticles in UI (search filter)
    allowedArticleChangesMap = new Map<string, boolean>(); // map of articleId and boolean if changes are allowed on this article (basically the same as the availableArticles array, but mapped for faster change detection

    // job extensions
    chooseArticleModalVisible = false;
    currentJobExtensionFormGroups: Array<UntypedFormGroup> = []; // formgroups of current job extensions of this job (already existing (maybe changed) and newly created)
    jobExtensionIdsToDelete: Array<string> = []; // holds job extension ids which existed when the component was loaded, but were deleted by user afterwards
    jobExtensionFormGroupChangeSubscriptions: Array<Subscription> = []; // holds all change subscriptions of job extensions so that we know to update them in db if they change
    currentExpandedJobExtensions = []; // holds ids of all job extensions which are currently expanded in the accordion view

    articleHelpModalVisible = false;
    articleHelpEntries: Array<Attachment> = [];

    jobExtensionsLoading = true;
    reorderExtensionsEnabled = false;
    expandedJobExtensionsBeforeReorder: string[] = [];
    // job linkings
    @ViewChild('jobLinkingComp') jobLinkingComp: JobLinkingsComponent;

    // source job modal (opened from jobLinkChip on non-phone)
    sourceJobModalVisible = false;
    sourceJobModalJobData: Job = null;
    sourceJobModalServiceGroupData: ServiceGroup = null;
    sourceJobModalProjectData = null;

    // temporary photos (photo-documentation-only mode)
    @ViewChild('photoDocumentationAutoAttachmentSelect') photoDocumentationAutoAttachmentSelectComp: AttachmentSelectComponent;
    temporaryPhotos: Array<JobPhoto> = []; // used for photo-documentation-only mode where photos are taken before an article is selected
    autoCameraIsOpen = false;

    // outputs
    @Output() jobWasSaved = new EventEmitter<{ job: Job; closeJobModal: boolean }>();

    // helper variables
    currentMobileView: 'job-data' | 'job-photos' | 'job-building-plan' | 'job-code' = 'job-data';
    currentJobExtensionSortingMode: 'asc' | 'desc' = 'asc';
    currentBuildingPlanName = '';
    articleSearchBarCurrentValue = '';
    buildingPlanListSubscription: Subscription;
    possibleDataRecordingSettings = DataRecordingSetting;

    jobFormGroupChangesSubscription: Subscription;
    jobFormGroupCodeChangesSubscription: Subscription;

    linkedJobsLoading = true;
    initialLinkedJobIds: Array<string> = []; // used to track changes to linkedJobs, only set during init so we know what has changed
    removedLinkedJobIdsFromFields: Array<string> = []; // tracks job IDs removed from linking-type fields (to clear referringSourceJobId on save)

    buildingPlanModalVisible = false; // used on desktop
    showDialogPlanView = false;

    dueDateModalVisible = false;
    dueDatesChanged = false;

    scanEnabled = false;
    blockSave = false; // Used to prevent updates e.g. if code is not unique
    isViewer = false;

    hidePhotoSegment = false;
    hideCodeSegment = false;
    hidePlanSegment = false;

    serviceGroupPhotoCategoriesCount = 0; // how many photo categories the current service group has
    projectServiceGroupCount = 1;

    canGenerateCustomQRCode = false; // true if custom qr code generation is allowed
    currentlyGeneratingQrCode = false;

    private keyboardDidShowFn = (e) => {
        // for future purpose
    };
    private keyboardDidHideFn = () => {
        // Update change detection on keyboard close
        this.cdRef.detectChanges();
    };

    private destroyed$: ReplaySubject<boolean>; // to automatically disable running subscriptions on destroy

    constructor(
        private projectSrv: ProjectService,
        public utils: UtilsService,
        public settingsSrv: SettingsService,
        private logger: LoggerService,
        private modalCtrl: ModalController,
        private authSrv: AuthService,
        private db: DbService,
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotificationService,
        private fb: UntypedFormBuilder,
        private storage: StorageService,
        private cdRef: ChangeDetectorRef,
        private http: HttpClient,
        private translate: TranslateService
    ) {
        this.destroyed$ = new ReplaySubject<boolean>(1);
    }

    // When working on desktop this code only runs once due to the nature of working with a split pane
    // while on smartphones this runs everytime a job is opened cause we use ion-modal for displaying details
    ngOnInit() {
        this.logger.log('[JOB DETAILS] init - showing details for job', this.job);
        this.initJobForm();

        // subscribe to building plan list changes
        this.buildingPlanListSubscription = this.projectSrv.currentProjectBuildingPlanList$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((bpList) => {
                this.buildingPlanList = bpList.filter((bp) => bp.dataStatus == PlanStatus.Done);

                this.initJobBuildingPlan();
            });

        // check user rights
        this.isViewer = this.authSrv.isViewerLicense;
        this.changesAllowed =
            this.settingsSrv.currentUserRights.canManageProjects === true ||
            this.projectSrv.serviceGroupChangesAllowedMap.get(this.serviceGroup._id);
        this.currentUserID = this.authSrv.authInfo.userData.userName;

        // init possible workers
        this.possibleWorkers = [...this.authSrv.authInfo.companyUsers];

        this.prepareArticles();
        this.checkCustomQRCodeGenerationAllowed();

        window.addEventListener('keyboardDidShow', this.keyboardDidShowFn);
        window.addEventListener('keyboardDidHide', this.keyboardDidHideFn);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.job && !changes.job.isFirstChange()) {
            // the job input has changed (because the component was already created, but the user select a new job from the list) - therefore we initialize the new job form
            this.logger.log('[JOB DETAILS] job input changed - showing details for job', this.job);
            this.reorderExtensionsEnabled = false;
            this.initJobForm();
            this.initJobBuildingPlan();
            this.resetFilterArticles();
        }
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        // the destroyed$ subject itself lets all observables unsubscribe but for good measure we remove possible strays manually as well
        this.currentJobExtensionFormGroups = [];
        this.cleanupFormChangeSubscriptionsForJobExtensions();

        window.removeEventListener('keyboardDidShow', this.keyboardDidShowFn);
        window.removeEventListener('keyboardDidHide', this.keyboardDidHideFn);
    }

    /**
     * Sets given fields based on current available autoFillFields
     * @param fieldsToSet
     */
    async autoFillFields(fieldsToSet: Field[]) {
        try {
            // load auto-fill fields from local storage, save them in-memory for usage in this component
            let autoFillFields = [];
            let savedAutoFillFieldData = await this.storage.get('dp_autofill_fields');
            if (Array.isArray(savedAutoFillFieldData) && savedAutoFillFieldData.length > 0) {
                autoFillFields = savedAutoFillFieldData;
            }

            for (let field of fieldsToSet) {
                if (field.autoFillLastValue) {
                    // check if we can find this field in our last auto fill fields, then take value from there
                    let foundAutoFillField = autoFillFields.find((x) => x.fieldName === field.name);
                    if (foundAutoFillField != null) {
                        if (field.value == null || field.value === '' || (Array.isArray(field.value) && field.value.length === 0)) {
                            if (field.type === FieldType.Select) {
                                // if the field type is select we need to check if the value we want to fill is included in possibleValues
                                let found = false;
                                for (let entry of field.possibleValues) {
                                    if (entry.value === foundAutoFillField.fieldValue) {
                                        found = true;
                                        break;
                                    }
                                }
                                if (found) {
                                    field.value = foundAutoFillField.fieldValue;
                                }
                            } else {
                                // default handling - just set value
                                field.value = foundAutoFillField.fieldValue; // only set field.value if it currently is not set
                            }
                        }
                    }
                } else if (field.type === FieldType.Group) {
                    // on a group we don't have autoFill set so we have to check recursively in subfields
                    await this.autoFillFields(field.subFields);
                }
            }
        } catch (autoFillError) {
            this.logger.error('[JOB DETAILS] could not auto fill fields', autoFillError);
        }
    }

    /**
     * Initializes the job form
     * @param fullInit if set to false will not trigger certain actions (used on e.g. auto saves)
     */
    async initJobForm(fullInit = true) {
        // load autofill fields before loading job
        if (fullInit) {
            await this.autoFillFields(this.job.fields);
        }

        this.jobFormGroup = this.fb.group({
            name: this.fb.control(this.job.name),
            jobNumber: this.fb.control(this.job.jobNumber),
            code: this.fb.control(this.job.code),
            date: this.fb.control(this.job.date),
            worker: this.fb.control(this.job.worker),
            articles: this.utils.arrayToFormArray(this.job.articles),
            fields: this.utils.arrayToFormArray(this.job.fields),
            externalJobId: this.fb.control(this.job.externalJobId),
        });

        this.logger.log('[JOB DETAILS] form initialized', this.jobFormGroup);

        // if we are on mobile check on which tab we want to start
        if (this.utils.isPhone && fullInit) {
            if (!this.hidePlanSegment && this.serviceGroup?.settings?.dataRecording?.startMobileDocumentationOnPlan) {
                this.currentMobileView = 'job-building-plan';
            } else {
                this.currentMobileView = 'job-data';
            }
        }

        // calculate how wide our (old deprecated) job photo column can be (based on how many photo categories there are)
        if (this.serviceGroup.settings.dataRecording.photoCategories.length <= 3) {
            this.serviceGroupPhotoCategoriesCount = this.serviceGroup.settings.dataRecording.photoCategories.length;
        } else {
            this.serviceGroupPhotoCategoriesCount = 3;
        }

        // save how many service groups we have in our project
        this.projectServiceGroupCount = this.projectSrv.currentProjectServiceGroupList$.getValue().length;

        if (fullInit) {
            // init job extensions
            this.initializeJobExtensions();

            // init linked jobs
            this.initialLinkedJobIds = this.utils.clone(this.job.linkedJobIds); // save so we can check which linked jobs changed
        }

        // listen to form changes to trigger certain actions
        this.setupFormChangeSubscriptions();

        // auto add article to empty job if only one article available (and no photo-documentation only)
        try {
            if (this.job.jobExtensions.length === 0 && this.job.articles.length === 0) {
                if (
                    !this.settingsSrv.photoDocumentationOnly ||
                    !this.settingsSrv.vendingOnly ||
                    !this.settingsSrv.getCompanySettings().startPhotoDocumentationWithCamera
                ) {
                    if (this.availableArticles.length === 1) {
                        this.onArticleChosen(this.availableArticles[0]);
                    } else {
                        let autoCreateArticleId = this.serviceGroup.settings.dataRecording.autoCreateArticle;
                        if (autoCreateArticleId != null) {
                            let foundArticle = this.availableArticles.find((x) => x._id === autoCreateArticleId);
                            if (foundArticle != null) {
                                this.onArticleChosen(foundArticle);
                            }
                        }
                    }
                }
            }
        } catch (articleAddErr) {
            this.logger.error('[JOB DETAILS] could not auto add article', articleAddErr);
        }
    }

    /**
     * Update visible articles based on service group rights
     */
    private prepareArticles() {
        this.availableArticles = [];
        this.allowedArticleChangesMap.clear();

        // this.serviceGroup = sg;
        for (let article of this._serviceGroup.articles) {
            if (
                this.settingsSrv.currentUserRights.canManageProjects ||
                article.allowedLicenseTypes.length === 0 ||
                article.allowedLicenseTypes.includes(this.authSrv.authInfo.userData.licenseType)
            ) {
                this.availableArticles.push(article);
                this.allowedArticleChangesMap.set(article._id, true);
            } else {
                this.allowedArticleChangesMap.set(article._id, false);
            }
        }

        this.filteredAvailableArticles = this.availableArticles;

        // calculate how wide our (old deprecated) job photo column can be (based on how many photo categories there are)
        if (this.serviceGroup.settings.dataRecording.photoCategories.length <= 3) {
            this.serviceGroupPhotoCategoriesCount = this.serviceGroup.settings.dataRecording.photoCategories.length;
        } else {
            this.serviceGroupPhotoCategoriesCount = 3;
        }
    }

    /**
     * Creates subscriptions for handling form changes depending on being a phone or tablet/desktop
     */
    private setupFormChangeSubscriptions() {
        // if the job form changes we need to re-calculate the status
        // clear previous subscription first
        if (this.jobFormGroupChangesSubscription) {
            this.jobFormGroupChangesSubscription.unsubscribe();
        }
        this.jobFormGroupChangesSubscription = this.jobFormGroup.valueChanges
            .pipe(takeUntil(this.destroyed$), debounceTime(500))
            .subscribe((currentValue) => {
                if (this.job && this.serviceGroup) {
                    this.refreshJobStatus('data');
                }
            });

        // Keep track of changes to the job code and prevent using double codes
        // cleara previous subscription first
        if (this.jobFormGroupCodeChangesSubscription) {
            this.jobFormGroupCodeChangesSubscription.unsubscribe();
        }
        this.jobFormGroupCodeChangesSubscription = this.jobFormGroup
            .get('code')
            .valueChanges.pipe(
                takeUntil(this.destroyed$),
                tap((currentCode) => {
                    this.blockSave = true; // preemptively prevent saving changes while we check the code
                }),
                debounceTime(this.utils.isPhone ? 1400 : 800), // Inputs on mobile devices are slower than with a keyboard so we debounce for longer time
                switchMap((currentCode) => {
                    if (currentCode === '') {
                        // empty codes count as duplicate due to other jobs being possible to have no code as well
                        return of(true);
                    }
                    return this.projectSrv.isCodeUnused(currentCode);
                })
            )
            .subscribe({
                next: (isUnused) => {
                    if (this.utils.isPhone) {
                        this.refreshJobStatus('code');
                    }

                    // Prevent the user from updating saving the job when using non unqiue cdoe
                    this.blockSave = !isUnused;
                    if (!isUnused) {
                        this.logger.warn('[JOB DETAILS] double code found');
                        this.notify.warn(
                            this.translate.instant('projects.jobs.info.double-code-desc'),
                            this.translate.instant('projects.jobs.info.double-code-title')
                        );
                    }
                },
                error: (searchErr) => {
                    this.logger.error('[JOB DETAILS] Failed searching for existing job code ', searchErr);
                    this.blockSave = false; // stop blocking save in case of error
                    this.utils.sentryCaptureException(searchErr);
                },
            });
    }

    /**
     * Initializes job extensions of current job
     */
    async initializeJobExtensions() {
        this.logger.log('[JOB DETAILS] loading job extensions', this.job.jobExtensions);

        try {
            // get job extension sorting mode (only if reordering is not allowed, otherwise always use asc)
            if (this.serviceGroup.settings.dataRecording.extensionOrderFixed) {
                let sortingMode = await this.storage.get('dp_job_ext_sorting_mode');
                if (sortingMode === 'asc' || sortingMode === 'desc') {
                    this.currentJobExtensionSortingMode = sortingMode;
                }
            }
        } catch (sortingErr) {
            this.logger.error('[JOB DETAILS] could not get job extension sorting mode from storage', sortingErr);
        }

        // init variables
        this.currentJobExtensionFormGroups = [];
        this.jobExtensionIdsToDelete = [];
        this.jobExtensionsLoading = true;

        // cleanup existing change subscriptions if there should be any
        this.cleanupFormChangeSubscriptionsForJobExtensions();

        if (this.job.jobExtensions.length > 0) {
            let jobExtensionsArr = this.utils.clone(this.job.jobExtensions) as Array<JobExtensionInfo>;
            if (this.currentJobExtensionSortingMode === 'desc') {
                jobExtensionsArr.reverse();
            }
            let jobExtensionObservables = jobExtensionsArr.map((jobExtensionInfo) => {
                return this.db.getItemById(jobExtensionInfo._id).pipe(
                    takeUntil(this.destroyed$), // kill requests if the component gets exited in the meantime
                    catchError((err) => {
                        this.logger.error(
                            `[JOB DETAILS] failed to load job extension ${jobExtensionInfo._id} of job ${this.job._id} from db`,
                            err
                        );
                        return of('failed');
                    })
                );
            });

            forkJoin(jobExtensionObservables).subscribe(
                (res) => {
                    this.logger.log(`[JOB DETAILS] loaded job extensions from db`, res);
                    for (let extension of res) {
                        if (extension === 'failed') {
                            this.logger.log(`[JOB DETAILS] skipping init of job extension as it failed loading from db`);
                            continue;
                        }
                        let jobExtensionModel = new JobExtension(extension);

                        // only load job-extensions which are correctly linked to this job
                        if (jobExtensionModel.relatedJob === 'job#' + this.job._id) {
                            let jobExtensionFormGroup = this.utils.objectToFormGroup(jobExtensionModel);
                            this.setupFormChangeSubscriptionForJobExtension(jobExtensionFormGroup);
                            this.currentJobExtensionFormGroups.push(jobExtensionFormGroup);
                            //this.existingJobExtensionFormGroups.push(jobExtensionFormGroup);
                        }
                    }
                    if (this.serviceGroup.settings.dataRecording.serviceCollapse != DataCollapseSetting.Default) {
                        // Apply collapse setting defined in service group
                        switch (this.serviceGroup.settings.dataRecording.serviceCollapse) {
                            case DataCollapseSetting.None:
                                this.currentExpandedJobExtensions = [];
                                break;
                            case DataCollapseSetting.All:
                            default:
                                this.currentExpandedJobExtensions = this.currentJobExtensionFormGroups.map((x) => x.value._id); // same as below when using company setting
                                this.handleAccordionToggle(this.currentExpandedJobExtensions);
                                break;
                        }
                    } else if (
                        // if we have more than 10 job extensions in this job, we collapse them by default to prevent performance issues / hangs
                        this.currentJobExtensionFormGroups.length <= 10 ||
                        this.settingsSrv.getCompanySettings().disableAutoJobExtensionAccordionToggle
                    ) {
                        this.currentExpandedJobExtensions = this.currentJobExtensionFormGroups.map((x) => x.value._id);
                    }
                    this.jobExtensionsLoading = false;
                },
                (err) => {
                    this.logger.error(`[JOB DETAILS] could not load job extensions of job ${this.job._id} from db`, err);
                    this.notify.error(this.translate.instant('projects.jobs.errors.load-job-err'));
                    this.jobExtensionsLoading = false;
                },
                () => {}
            );
        } else {
            this.logger.log('[JOB DETAILS] this job has no job extensions');
            this.jobExtensionsLoading = false;

            this.autoOpenCamera(); // open camera automatically if setting is active

            // refresh job status, because we may have old articles if we dont have job extensions
            this.refreshJobStatus();
        }
    }

    /**
     * Cleans currently running form-change subscriptions for job-extensions
     */
    cleanupFormChangeSubscriptionsForJobExtensions() {
        for (let subscription of this.jobExtensionFormGroupChangeSubscriptions) {
            if (subscription) {
                subscription.unsubscribe();
            }
        }
        this.jobExtensionFormGroupChangeSubscriptions = [];
    }

    /**
     * Takes given jobExtension formGroup and setsup value changes subscription for save-handling
     * @param jobExtensionFormGroup
     */
    setupFormChangeSubscriptionForJobExtension(jobExtensionFormGroup: FormGroup) {
        // The debounce is needed to keep our UI in performant state but increasing it can lead to missed updates when users are too quick in saving changes - for example taking pictures and instantly saving can lead to loss of the change on the extension
        this.jobExtensionFormGroupChangeSubscriptions.push(
            jobExtensionFormGroup.valueChanges.pipe(takeUntil(this.destroyed$), debounceTime(150)).subscribe(() => {
                // mark the formgroup as dirty so we know that we need to update it in db
                jobExtensionFormGroup.markAsDirty();

                // refresh the data completion status as we display it
                this.refreshJobStatus('data-photos');
            })
        );
    }

    /**
     * Loads the building plan, if the job has one
     */
    async initJobBuildingPlan(keepMapLoaded = false): Promise<void> {
        let lastUsedPlanId: string;

        try {
            lastUsedPlanId = await this.storage.get('dp_job_lastused_bp');
        } catch (lastUseEx) {
            this.logger.warn('[JOB DETAILS] could not get last used building plan id from storage', lastUseEx);
        }

        this.jobBuildingPlan = null;
        this.showDialogPlanView = keepMapLoaded;

        if (((this.job.buildingPlanId && this.job.buildingPlanId != '') || lastUsedPlanId) && this.buildingPlanList.length > 0) {
            if (this.job.buildingPlanId == null) {
                this.logger.log('[JOB DETAILS] the building plan was cleared by the user');
                return;
            } else if (this.job.buildingPlanId != '') {
                this.jobBuildingPlan = this.buildingPlanList.find((x) => x._id == this.job.buildingPlanId);
            } else if (lastUsedPlanId != '') {
                this.jobBuildingPlan = this.buildingPlanList.find((x) => x._id == lastUsedPlanId);
            }

            if (!this.jobBuildingPlan) {
                this.notify.error(this.translate.instant('projects.jobs.errors.bp-not-found'));
                this.utils.sentryCaptureMessage('[JOB DETAILS] Could not find job');
            }
        }
    }

    /**
     * Triggered when the user switches the mobile view
     * @param data
     */
    onChangeMobileView(data) {
        this.currentMobileView = data.detail.value;
    }

    /**
     * Triggered when the user opens the building plan dialog on desktop
     */
    onOpenDesktopBuildingPlanDialog() {
        this.showDialogPlanView = true;
        this.buildingPlanModalVisible = true;
    }

    /**
     * Triggered when a building plan is selected
     * @param buildingPlanId
     */
    async onSelectBuildingPlan(buildingPlanId: string): Promise<void> {
        // When opening the map the first time it fires the current selected plan
        // this is needed in situations where the job itself has no plan marking yet but a routine like remembering the
        // last plan prefills the plan value which should only be saved when the user does this intentionally
        if (this.job.buildingPlanId != buildingPlanId) {
            this.job.marker = [];
        }
        this.job.buildingPlanId = buildingPlanId;
        await this.initJobBuildingPlan(this.showDialogPlanView);

        await this.storage.set('dp_job_lastused_bp', buildingPlanId);
    }

    /**
     * @deprecated should not be used anymore
     * Updates the current job building plan name by id if the job has a building plan selected
     */
    updateCurrentBuildingPlanName(): void {
        if (this.job.buildingPlanId != '') {
            let selectedBp = this.buildingPlanList.find((x) => x._id == this.job.buildingPlanId);
            this.currentBuildingPlanName = selectedBp ? selectedBp.name : '';
        }
    }

    /**
     * Triggered when the user confirms the marker position on the plan
     * Currently no extra handling needed as the marker is directly stored to the bound job
     * @param planDataChanged
     */
    async onSaveMarker(planDataChanged: boolean) {
        this.logger.log('[JOB DETAILS] Marker confirmed', this.job.marker);
        this.buildingPlanModalVisible = false;
    }

    /**
     * Triggered when the user wants to remove the current job position marking
     */
    async onRemovePlanMarking() {
        this.notify
            .confirm(
                this.translate.instant('projects.jobs.info.delete-markers-desc'),
                this.translate.instant('projects.jobs.info.delete-markers-title'),
                this.translate.instant('projects.jobs.info.delete-markers-confirm')
            )
            .then(() => {
                this.job.marker = [];
                this.job.buildingPlanId = ''; // important to set it to an empty string!
                this.initJobBuildingPlan();
                this.buildingPlanModalVisible = false;
            })
            .catch(() => {
                // cancel button
            });
    }

    /**
     * Triggered when the user wants to add an article (job extension) to the job. Shows the article selection grid as dialog
     */
    onAddJobExtension() {
        this.resetFilterArticles();
        this.chooseArticleModalVisible = true;
    }

    /**
     * Triggered when the user has chosen an article to add. Hides dialog and adds given article as a job extension
     */
    async onArticleChosen(article: Article) {
        const chosenArticle = new Article(article, true);
        chosenArticle.pricing = new ArticlePricing(); // reset pricing info as we dont need to store it on our job

        // get the current user name
        let workerName = this.authSrv.authInfo.userData.userName;
        if (this.authSrv.authInfo.userData.firstName != '' || this.authSrv.authInfo.userData.lastName != '') {
            workerName = this.authSrv.authInfo.userData.firstName + ' ' + this.authSrv.authInfo.userData.lastName;
        }

        // if temporary photos are saved (because photo-documentation-only is active) add the photos to the selected article
        if (this.temporaryPhotos.length > 0) {
            for (let photo of this.temporaryPhotos) {
                chosenArticle.photos.push(photo);
            }
            this.temporaryPhotos = []; // reset
        }

        // auto fill article fields if there are any
        await this.autoFillFields(chosenArticle.fields);

        let newJobExtension = new JobExtension({
            article: chosenArticle,
            worker: workerName,
            createdBy: this.authSrv.authInfo.userData.userName,
        });
        this.logger.log('[JOB DETAILS] new article chosen. preparing new job extension', newJobExtension);

        let newJobExtensionFormGroup = this.utils.objectToFormGroup(newJobExtension);
        newJobExtensionFormGroup.markAsDirty(); // used to always save new job extension on jobSave
        if (this.currentJobExtensionSortingMode === 'asc') {
            this.currentJobExtensionFormGroups.push(newJobExtensionFormGroup);
        } else {
            this.currentJobExtensionFormGroups.unshift(newJobExtensionFormGroup);
        }
        this.setupFormChangeSubscriptionForJobExtension(newJobExtensionFormGroup);

        // we have to actually set this variable and cannot just push the new id (because of change detection) - this is done in a helper function to check if we have some other accordions to close
        this.handleAccordionToggle([...this.currentExpandedJobExtensions, newJobExtensionFormGroup.value._id]);

        this.chooseArticleModalVisible = false;

        // maybe the segments need a change of visibility
        this.updateSegmentVisibility();

        // force map status update for the case of a general icon on the article without icon on the fields themselves
        this.refreshJobStatus('marker');

        // scroll the job-extension we added into view
        setTimeout(() => {
            if (this.currentJobExtensionSortingMode === 'asc') {
                Array.from(document.querySelectorAll('.job-extensions-wrap app-job-extension')).pop()?.scrollIntoView(true);
            } else {
                Array.from(document.querySelectorAll('.job-extensions-wrap app-job-extension:first-child')).pop()?.scrollIntoView(true);
            }
        }, 100);
    }

    /**
     * Triggered when the user wants to duplicate a job extension
     */
    async onDuplicateJobExtension(jobExtensionFormGroup: UntypedFormGroup) {
        // get the current user name
        let workerName = this.authSrv.authInfo.userData.userName;
        if (this.authSrv.authInfo.userData.firstName != '' || this.authSrv.authInfo.userData.lastName != '') {
            workerName = this.authSrv.authInfo.userData.firstName + ' ' + this.authSrv.authInfo.userData.lastName;
        }
        let newJobExtension = await this.projectSrv.duplicateJobExtension(jobExtensionFormGroup.value, workerName);

        let newJobExtensionFormGroup = this.utils.objectToFormGroup(newJobExtension);
        newJobExtensionFormGroup.markAsDirty(); // used to always save new job extension on jobSave
        //this.createdJobExtensionFormGroups.push(newJobExtensionFormGroup);
        this.logger.log('[JOB DETAILS] job extension duplicated.', jobExtensionFormGroup.value, newJobExtension);
        if (this.currentJobExtensionSortingMode === 'asc') {
            this.currentJobExtensionFormGroups.push(newJobExtensionFormGroup);
        } else {
            this.currentJobExtensionFormGroups.unshift(newJobExtensionFormGroup);
        }
        this.setupFormChangeSubscriptionForJobExtension(newJobExtensionFormGroup);
        // let the helper handle the addition to the array - setting a new array is needed due to change detection
        this.handleAccordionToggle([...this.currentExpandedJobExtensions, newJobExtensionFormGroup.value._id]);

        // scroll the job-extension we added into view
        setTimeout(() => {
            if (this.currentJobExtensionSortingMode === 'asc') {
                Array.from(document.querySelectorAll('.job-extensions-wrap app-job-extension')).pop()?.scrollIntoView(true);
            } else {
                Array.from(document.querySelectorAll('.job-extensions-wrap app-job-extension:first-child')).pop()?.scrollIntoView(true);
            }
        }, 100);
    }

    /**
     * Triggered when the user wants to delete an article (job extension)
     */
    onDeleteJobExtension(extensionIndex: number) {
        this.jobExtensionIdsToDelete.push(this.currentJobExtensionFormGroups[extensionIndex].value._id);
        this.currentJobExtensionFormGroups.splice(extensionIndex, 1);

        this.updateSegmentVisibility();
    }

    /**
     * Open article help modal
     * @param article
     */
    onOpenArticleHelpModal(article: Article) {
        this.articleHelpEntries = []; // first reset to an empty array

        try {
            if (Array.isArray(article.articleHelp)) {
                this.articleHelpEntries = article.articleHelp;
            }

            let foundServiceGroupArticle = this.serviceGroup.articles.find((x) => x.name == article.name);
            if (foundServiceGroupArticle != null && Array.isArray(foundServiceGroupArticle.articleHelp)) {
                this.articleHelpEntries = foundServiceGroupArticle.articleHelp;
            }
        } catch (articleHelpErr) {
            this.logger.error('[JOB DETAILS] no article help found', articleHelpErr);
        }

        // open modal
        this.articleHelpModalVisible = true;
    }

    /**
     * Triggered when the user wants to refresh job extension fields
     * @param jobExtensionFormGroup
     * @param jobExtensionComponent
     */
    onRefreshJobExtensionFields(jobExtensionFormGroup: UntypedFormGroup, jobExtensionComponent: JobExtensionComponent) {
        try {
            // find article in our current service group so we can get the refreshed data
            let articleFound = this._serviceGroup.articles.find((x) => x._id === jobExtensionFormGroup.get('article').get('_id').value);
            if (articleFound != null) {
                // build field value mapping array so we can put the current field values in our updated fields array after swapping the array
                let fieldValueMapping = [];
                for (let fieldGroup of (<UntypedFormArray>jobExtensionFormGroup.get('article').get('fields')).controls) {
                    fieldValueMapping = [...fieldValueMapping, ...this.getFieldValueForFieldMapping(fieldGroup)];
                }
                fieldValueMapping = fieldValueMapping.filter((x) => x.value != null && x.value != ''); // filter out empty values, we dont need to set them again

                // remove old fields form array
                (<UntypedFormGroup>jobExtensionFormGroup.get('article')).removeControl('fields');
                // add new fields form array
                (<UntypedFormGroup>jobExtensionFormGroup.get('article')).addControl(
                    'fields',
                    this.utils.arrayToFormArray(articleFound.fields)
                );

                // loop through new fields array and fill out old values
                for (let fieldGroup of (<UntypedFormArray>jobExtensionFormGroup.get('article').get('fields')).controls) {
                    this.setFieldValueFromMappingArray(fieldGroup, fieldValueMapping);
                }

                try {
                    let allPhotos = this.utils.clone((<UntypedFormArray>jobExtensionFormGroup.get('article').get('photos')).value);

                    const categoriesWithPhotos = new Set(allPhotos.map((p) => p.photoCategoryName).filter(Boolean));
                    const filteredCategories = articleFound.photoCategories
                        .concat(
                            allPhotos
                                .map((p) => p.photoCategoryName)
                                .filter((catName) => !articleFound.photoCategories.some((c) => c.categoryName === catName))
                                .filter((value, index, self) => self.indexOf(value) === index) // avoid duplicates
                                .map((catName: string) => new ServiceGroupPhotoCategory({ categoryName: catName, readonly: true }))
                        )
                        // only delete a category if it is not in the template and no photos exist
                        .filter(
                            (c) =>
                                categoriesWithPhotos.has(c.categoryName) ||
                                articleFound.photoCategories.some((ac) => ac.categoryName === c.categoryName)
                        );

                    // refresh article photos
                    // remove old photo categories form array
                    (<FormGroup>jobExtensionFormGroup.get('article')).removeControl('photoCategories');
                    // add new fields form array
                    (<FormGroup>jobExtensionFormGroup.get('article')).addControl(
                        'photoCategories',
                        this.utils.arrayToFormArray(filteredCategories)
                    );
                } catch (removePhotosErr) {
                    this.logger.error('[JOB DETAILS] cannot proceed field refreshing for photo categories');
                }

                try {
                    let duplicatePhotos = articleFound.duplicatePhotos || DuplicatePhotoSetting.Never;
                    let fieldsToIgnore = articleFound.fieldsToIgnoreOnDuplication || [];

                    jobExtensionFormGroup.get('article')?.get('duplicatePhotos')?.setValue(duplicatePhotos);

                    let fieldsToIgnoreFormArray = jobExtensionFormGroup.get('article')?.get('fieldsToIgnoreOnDuplication') as FormArray;
                    fieldsToIgnoreFormArray.clear(); // clear formArray
                    fieldsToIgnore.forEach((value) => fieldsToIgnoreFormArray.push(new FormControl(value)));
                } catch (err) {
                    this.logger.error('[JOB DETAILS] cannot proceed setting refreshing for article');
                }

                jobExtensionComponent.triggerFieldVisibilityCalculation();

                this.notify.info(this.translate.instant('projects.jobs.info.refreshed-fields'));
            }
        } catch (refreshErr) {
            this.logger.error('[JOB DETAILS] could not refresh job extension fields', refreshErr);
        }
    }

    /**
     * Triggered when the user wants to refresh job additional fields
     */
    onRefreshJobAdditionalFields() {
        try {
            // build field value mapping array so we can put the current field values in our updated fields array after swapping the array
            let fieldValueMapping = [];
            for (let fieldGroup of (<UntypedFormArray>this.jobFormGroup.get('fields')).controls) {
                fieldValueMapping = [...fieldValueMapping, ...this.getFieldValueForFieldMapping(fieldGroup)];
            }

            fieldValueMapping = fieldValueMapping.filter((x) => x.value != null && x.value != ''); // filter out empty values, we dont need to set them again

            // remove old fields form array
            (<UntypedFormGroup>this.jobFormGroup).removeControl('fields');
            // add new fields form array
            (<UntypedFormGroup>this.jobFormGroup).addControl('fields', this.utils.arrayToFormArray(this._serviceGroup.fields));

            // loop through new fields array and fill out old values
            for (let fieldGroup of (<UntypedFormArray>this.jobFormGroup.get('fields')).controls) {
                this.setFieldValueFromMappingArray(fieldGroup, fieldValueMapping);
            }

            this.jobAdditionalFieldsComponent?.calculateFieldVisibility();

            this.notify.info(this.translate.instant('projects.jobs.info.refreshed-fields'));
        } catch (refreshErr) {
            this.logger.error('[JOB DETAILS] could not refresh job additional fields', refreshErr);
        }
    }

    /**
     * Helper function used in refreshJobExtensionFields/refreshJobAdditionalFields to build a nested/recursive field mapping array
     * @param fieldFormGroup
     */
    private getFieldValueForFieldMapping(fieldFormGroup: AbstractControl) {
        let returnFieldValues = [];
        if (fieldFormGroup.get('type').value === 'group') {
            let subFields = fieldFormGroup.get('subFields') as FormArray;
            for (let subField of subFields.controls) {
                let returnSub = this.getFieldValueForFieldMapping(subField);
                returnSub.map((x) => (x.name = fieldFormGroup.get('name').value + '->' + x.name));
                returnFieldValues = [...returnFieldValues, ...returnSub];
            }
        } else {
            returnFieldValues.push({
                _id: fieldFormGroup.get('_id').value,
                name: fieldFormGroup.get('name').value,
                type: fieldFormGroup.get('type').value,
                value: fieldFormGroup.get('value').value,
                multiSelect: fieldFormGroup.get('multiSelect').value,
                products: fieldFormGroup.get('selectedProducts')?.value,
            });
        }
        return returnFieldValues;
    }

    /**
     * Helper function used in refreshJobExtensionFields to set nested/recursive field values based on built mapping array
     * @param fieldFormGroup
     * @param mappingArray
     * @param parentFieldPrefix
     * @private
     */
    private setFieldValueFromMappingArray(fieldFormGroup: AbstractControl, mappingArray: any[], parentFieldPrefix = '') {
        if (fieldFormGroup.get('type').value === 'group') {
            // set field values for subfields
            let subFields = fieldFormGroup.get('subFields') as FormArray;
            for (let subfield of subFields.controls) {
                this.setFieldValueFromMappingArray(subfield, mappingArray, parentFieldPrefix + fieldFormGroup.get('name').value + '->');
            }
        } else {
            let fieldName = parentFieldPrefix + fieldFormGroup.get('name').value;
            let mappingElements = mappingArray.filter((x) => x.name === fieldName);
            if (mappingElements.length > 0) {
                // if there are more than 1 mapping elements => next we search for the _id
                let mappingElement = mappingElements[0];
                if (mappingElements.length > 1) {
                    mappingElement = mappingElements.find((x) => x._id === fieldFormGroup.get('_id').value);
                }

                // check if mapping element is not null
                if (mappingElement != null) {
                    if (
                        mappingElement.type === fieldFormGroup.get('type').value &&
                        mappingElement.multiSelect === fieldFormGroup.get('multiSelect').value
                    ) {
                        console.log('field name ' + fieldName, mappingElement.value);
                        // only fill out old value if the field type is still the same
                        fieldFormGroup.get('value').setValue(mappingElement.value);

                        if (
                            FieldType.ExtendableSelect === mappingElement.type &&
                            fieldFormGroup.get('possibleValues').value?.findIndex((pV: any) => pV.value == mappingElement.value) === -1
                        ) {
                            // We need to update the possible options or the value of the field will disappear
                            (fieldFormGroup.get('possibleValues') as UntypedFormArray).push(
                                this.utils.objectToFormGroup(
                                    new PossibleFieldValue({
                                        value: mappingElement.value,
                                    })
                                )
                            );
                        }

                        if (FieldType.Product === mappingElement.type) {
                            // For products we need to add the additional control
                            for (let productItem of mappingElement.products) {
                                (fieldFormGroup.get('selectedProducts') as UntypedFormArray).push(
                                    this.utils.objectToFormGroup(new Product(productItem, true))
                                );
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Helper function which returns the current job articles as form array (old deprecated)
     */
    getJobArticlesFormArray() {
        return this.jobFormGroup.get('articles') as UntypedFormArray;
    }

    /**
     * Helper function which returns the current job fields as form array
     */
    getJobFieldsFormArray() {
        return this.jobFormGroup.get('fields') as UntypedFormArray;
    }

    /**
     * Deletes the old job article (old deprecated article system, where articles were saved on job directly)
     */
    onDeleteOldJobArticle(articleIndex: number) {
        this.getJobArticlesFormArray().removeAt(articleIndex);
    }

    /**
     * Triggered when the user has added a job link. Saves the job automatically without closing the job dialog on mobile devices
     * @param data
     */
    async onSaveJobLinkAdded(data: { linkType: 'existing' | 'new'; job: Job }) {
        this.logger.log('[JOB DETAILS] added job link', data);
        this.job.linkedJobIds = [...this.job.linkedJobIds, data.job._id];

        // save job (without closing modal)
        await this.onSaveJob(false);
    }

    /**
     * Triggered when the user clicks the source job chip — opens the referring source job in a modal
     */
    async onViewSourceJob() {
        try {
            let sourceJob;
            try {
                sourceJob = new Job(await lastValueFrom(this.db.getItemById(this.job.referringSourceJobId)));
            } catch (jobLoadErr) {
                if (jobLoadErr === 'not_found') {
                    try {
                        if (this.changesAllowed) {
                            await this.notify.confirm(
                                this.translate.instant('projects.jobs.details.linking.job-not-found-desc-1'),
                                this.translate.instant('projects.jobs.details.linking.job-not-found-title'),
                                this.translate.instant('projects.jobs.details.linking.delete-linking-btn'),
                                this.translate.instant('shared.cancel')
                            );

                            this.job.referringSourceJobId = null;
                            this.onSaveJob(false, false);
                        } else {
                            this.notify.error(this.translate.instant('projects.jobs.details.linking.job-not-found-desc-2'));
                        }
                    } catch (confirmCanceled) {}
                } else {
                    this.notify.error(this.translate.instant('projects.jobs.details.linking.job-open-err'));
                }
                return;
            }

            const visibleGroupIds = this.projectSrv.currentUserProjectSharingData.projectPermissions.visibleServiceGroups;
            const canSeeSourceJobSg =
                this.settingsSrv.currentUserRights.canManageProjects ||
                visibleGroupIds.length === 0 ||
                visibleGroupIds.includes(sourceJob.serviceGroupId);

            if (!canSeeSourceJobSg) {
                this.notify.error(this.translate.instant('projects.jobs.details.linking.job-open-rights-err'));
                return;
            }

            const sgData = new ServiceGroup(await lastValueFrom(this.db.getItemById(sourceJob.serviceGroupId)));
            if (sgData.syncedServiceGroup && sgData.originalId != null) {
                await this.projectSrv.loadOriginalServiceGroupForServiceGroup(sgData);
            }
            const projectData = await lastValueFrom(this.db.getItemById(sourceJob.projectId));

            if (!this.utils.isPhone) {
                this.sourceJobModalJobData = sourceJob;
                this.sourceJobModalServiceGroupData = sgData;
                this.sourceJobModalProjectData = projectData;
                this.sourceJobModalVisible = true;
                return;
            }

            const modal = await this.modalCtrl.create({
                component: JobDetailsComponent,
                componentProps: {
                    job: sourceJob,
                    project: projectData,
                    serviceGroup: sgData,
                    subView: true,
                    asDialog: true,
                    presentingElement: await this.modalCtrl.getTop(),
                },
                animated: true,
                handle: false,
                backdropDismiss: true,
                backdropBreakpoint: 1,
                initialBreakpoint: 1,
                breakpoints: [0, 1],
                canDismiss: async (data?: any, role?: string) => {
                    return role !== 'gesture';
                },
            });
            await modal.present();
            await modal.onWillDismiss();
        } catch (err) {
            this.logger.error('[JOB DETAILS] could not open source job', err);
        }
    }

    /**
     * Triggered when the source job modal saves — resets modal state
     */
    onSourceJobWasSaved(saveData: { job: Job; closeJobModal: boolean }) {
        if (saveData.closeJobModal) {
            this.sourceJobModalVisible = false;
            this.sourceJobModalJobData = null;
            this.sourceJobModalServiceGroupData = null;
            this.sourceJobModalProjectData = null;
        }
    }

    /**
     * Triggered when the user removes a linkedJob
     * @param index
     */
    async onRemoveJobLink(index: number) {
        let newLinkedJobIds = [...this.job.linkedJobIds];
        newLinkedJobIds.splice(index, 1);
        this.job.linkedJobIds = newLinkedJobIds;

        // save job (without closing modal)
        await this.onSaveJob(false);
    }

    /**
     * Triggered when a job link is removed from a linking-type field inside an extension
     */
    onJobLinkRemovedFromField(jobId: string) {
        if (jobId && !this.removedLinkedJobIdsFromFields.includes(jobId)) {
            this.removedLinkedJobIdsFromFields.push(jobId);
        }
    }

    /**
     * Triggered when a job link is added to a linking-type field inside an extension.
     * Cancels a pending removal so that referringSourceJobId is not wrongly cleared.
     */
    onJobLinkAddedToField(jobId: string) {
        const idx = this.removedLinkedJobIdsFromFields.indexOf(jobId);
        if (idx > -1) {
            this.removedLinkedJobIdsFromFields.splice(idx, 1);
        }
    }

    /**
     * Triggered from job-extension sub component if article changes occur to handle change events
     * @param jobExtensionFormGroup
     */
    onJobExtensionChanged(jobExtensionFormGroup) {
        jobExtensionFormGroup.markAsDirty();
    }

    async onLockRequested(lockBehavior: string, jobExtensionFormGroup) {
        if (lockBehavior === SignatureLockSetting.LockJobExtension) {
            jobExtensionFormGroup.get('locked')?.setValue(true);
            jobExtensionFormGroup.markAsDirty();
        } else if (lockBehavior === SignatureLockSetting.LockJob) {
            this.job.locked = true;
            await this.onSaveJob(false);
        }
    }

    /**
     * Triggered when the job photos changed. Updates the job status
     */
    async onPhotosChanged() {
        if (this.utils.isPhone) {
            this.refreshJobStatus('photos');
        }

        // and save our job after image changes
        await this.onSaveJob(false);
    }

    /**
     * Open camera automatically if photo documentation mode is active and setting to automatically open camera is set
     * Triggered automatically if job-extensions were loaded or attachmentSelect component is initialized
     */
    autoOpenCamera() {
        if (
            (this.settingsSrv.photoDocumentationOnly || this.settingsSrv.vendingOnly) &&
            this.settingsSrv.getCompanySettings().startPhotoDocumentationWithCamera &&
            this.photoDocumentationAutoAttachmentSelectComp != null &&
            this.job.jobExtensions.length === 0 &&
            !this.autoCameraIsOpen
        ) {
            this.autoCameraIsOpen = true;
            this.photoDocumentationAutoAttachmentSelectComp.onTakePhoto();
        }
    }

    /**
     * Triggered when the user adds the first photo (if setting is active to automatically open camera on job creation)
     */
    onPhotoDocumentationOnlyFirstPhotoAdded(photo: Attachment) {
        this.autoCameraIsOpen = false;
        this.logger.log('[JOB DETAILS] first job photo added (photo documentation only)', photo);
        let photoModel = new JobPhoto(photo);
        photoModel.photoCategoryName = 'Fotos'; // hardcoded
        this.temporaryPhotos.push(photoModel);
        this.onPhotosChanged();
        this.onAddJobExtension();
    }

    /**
     *
     * @param jobData
     */
    onMarkerChanged(jobData) {
        if (!jobData.jobMarker) {
            if (jobData.jobMarkerIdForDeletion != null && jobData.jobMarkerIdForDeletion.trim() !== '') {
                let foundJobMarkerIndex = this.job.marker.findIndex((x) => x._id === jobData.jobMarkerIdForDeletion);
                if (foundJobMarkerIndex > -1) {
                    this.job.marker.splice(foundJobMarkerIndex, 1);
                }
            }
        } else {
            let foundJobMarkerIndex = this.job.marker.findIndex((x) => x._id === jobData.jobMarker._id);
            if (foundJobMarkerIndex > -1) {
                this.job.marker[foundJobMarkerIndex] = jobData.jobMarker;
            }
        }
        if (this.utils.isPhone) {
            this.refreshJobStatus('map');
        }
    }

    /**
     * Triggered when the user wants to scan a qrcode, shows the scanner interface
     */
    async startScan() {
        this.scanEnabled = true;

        if (!this.utils.isPhone && !this.utils.isTablet) {
            this.notify.info(this.translate.instant('projects.jobs.info.scanner-not-available'));
        }
    }

    /**
     * Triggered when the scanner found a code. Sets the scanned code as the job qrcode
     * @param code
     */
    async onScanResult(code) {
        this.job.code = code;
        // the scanner does not update form group items by itself, we need to take care of it
        this.jobFormGroup.get('code').setValue(code);
    }

    /**
     * Triggered if the user changes the qrcode field manually
     * @param inputEvent
     */
    async onCodeChanged(inputEvent) {
        this.job.code = inputEvent.detail.value || '';
    }

    /**
     * Calculates the new job status because something has changed
     */
    refreshJobStatus(refresh: 'all' | 'data' | 'photos' | 'map' | 'code' | 'data-photos' | 'marker' = 'all') {
        // calculate job status and custom icons, save the status to the job
        let jobExtensionValues = [];
        for (let jobExtensionFormGroup of this.currentJobExtensionFormGroups) {
            jobExtensionValues.push(new JobExtension(jobExtensionFormGroup.value));
        }
        if (this.currentJobExtensionSortingMode === 'desc') {
            jobExtensionValues.reverse(); // before we calculate the job extension status the last-added job-extension always has to be the last entry in array
        }
        this.job.jobStatus = this.utils.calculateJobStatus(
            this.serviceGroup,
            new Job({ ...this.job, ...this.jobFormGroup.value }),
            jobExtensionValues,
            refresh
        );
    }

    /**
     * Lock job after job was signed
     */
    async onLockJobsAfterSigning() {
        if (this.serviceGroup && this.serviceGroup.settings.dataRecording.signatureLockBehavior == SignatureLockSetting.LockJob) {
            this.job.locked = true;
            await this.onSaveJob(false);
        }
    }

    /**
     * User manually locks/unlocks job if he has the right to do so
     */
    async onManuallyLockJob() {
        if (!this.changesAllowed) return;
        if (this.job.locked) {
            if (this.settingsSrv.currentUserRights.canUnlockJobs) {
                this.job.locked = false;
            }
        } else {
            if (this.settingsSrv.currentUserRights.canLockJobs) {
                if (!this.settingsSrv.currentUserRights.canUnlockJobs) {
                    try {
                        await this.notify.confirm(
                            this.translate.instant('projects.jobs.details.confirm-lock-no-unlock-right'),
                            this.translate.instant('projects.jobs.details.confirm-lock-title'),
                            this.translate.instant('projects.jobs.details.confirm-lock-btn')
                        );
                    } catch {
                        return;
                    }
                }
                this.job.locked = true;
                await this.onSaveJob(false);
            }
        }
    }

    /**
     * Saves the job in the db. Triggered when the user clicks the save button
     */
    async onSaveJob(closeJobModal = true, withoutSuccessNotify = false) {
        if (this.reorderExtensionsEnabled) {
            this.reorderExtensionsEnabled = false;
            this.currentExpandedJobExtensions = [...this.expandedJobExtensionsBeforeReorder];
            this.expandedJobExtensionsBeforeReorder = [];
        }

        if (this.blockSave) {
            this.logger.warn('[JOB DETAILS] job save called - not saving cause blockSave is true');
            return; // dont execute save code if block save is true (even autosaves must not execute save code because maybe a manual save is already running)
        }
        this.logger.log('[JOB DETAILS] starting job save process');
        this.blockSave = true;

        try {
            // handle saving of job extensions (changed or newly added)
            let createdJobExtensionInfos = []; // holds info of newly created job-extensions which we can save to our job afterwards
            for (let extensionFormGroup of this.currentJobExtensionFormGroups) {
                if (extensionFormGroup.dirty) {
                    // if the extensionformgroup is dirty the extension has changed or was just added
                    let extensionModel = new JobExtension(extensionFormGroup.value);

                    if (
                        extensionModel.relatedJob != null &&
                        extensionModel.relatedJob != '' &&
                        extensionModel.relatedJob != 'job#' + this.job._id
                    ) {
                        // its dead - keep data from pushing into save and log this
                        this.utils.sentryCaptureMessage('[JOB-DETAILS] Wanted to save extension which does not belong to this job', [
                            extensionModel._id,
                            extensionModel.relatedJob,
                            extensionModel.relation,
                            this.job._id,
                        ]);
                    } else {
                        // check if the job extension has a relation (maybe it was just created) then we have to set some properties like relation, etc.
                        if (
                            extensionModel.relatedJob == null ||
                            extensionModel.relatedJob == '' ||
                            extensionModel.relation == null ||
                            extensionModel.relation == ''
                        ) {
                            extensionModel.relation = 'project#' + this.project._id; // set relation to project (because we sync per project)
                            extensionModel.relatedJob = 'job#' + this.job._id; // set info to related job (extra property because we need the default relation property for project-wide sync)
                        }

                        // handle task ids
                        if (this.taskId != null && this.taskId.trim() !== '') {
                            if (Array.isArray(extensionModel.taskIds)) {
                                if (!extensionModel.taskIds.includes(this.taskId)) {
                                    extensionModel.taskIds.push(this.taskId);
                                }
                            } else {
                                extensionModel.taskIds = [this.taskId];
                            }
                        }

                        // push job extension into db
                        let extensionPut = await this.db.put(extensionModel).toPromise();
                        extensionModel = new JobExtension(extensionPut); // update with new version number and info we just got from db

                        // Track if this is a new extension (used later to close out old past due dates)
                        let isNewExtension = false;

                        // find the job extension info in our existing job and update the version number, as it has just changed
                        let jobExtensionInfoInJob = this.job.jobExtensions.find((x) => x._id === extensionModel._id);
                        if (jobExtensionInfoInJob != null) {
                            this.logger.log(
                                `[JOB DETAILS] existing job extension ${extensionModel._id} changed from version ${jobExtensionInfoInJob.version} to version ${extensionModel.version}`
                            );
                            jobExtensionInfoInJob.version = extensionModel.version;
                        } else {
                            isNewExtension = true;
                            // job extension was not found in job, so it probably was just created, add it to created job-extension infos
                            createdJobExtensionInfos.push({
                                _id: extensionModel._id,
                                version: extensionModel.version,
                            });
                            this.logger.log(
                                `[JOB DETAILS] new job extension ${extensionModel._id} added to job with version ${extensionModel.version}`
                            );

                            // check if article has notification which we need to create when it is added
                            if (extensionModel.article.articleAddedNotificationEnabled) {
                                let addNotifications = await this.projectSrv.prepareNotificationModels();
                                for (let notification of addNotifications) {
                                    notification.type = DpNotificationType.ARTICLE_ADDED;
                                    notification.name = extensionModel.article.name;
                                    notification.relatedJobExtension = extensionModel.pk;
                                    notification.relatedJobExtensionName = extensionModel.article.name;
                                    this.job.notificationsToCreate.push(notification);
                                }
                            }
                            // check if article has reminder which we need to set for the future
                            if (extensionModel.article.articleReminderEnabled) {
                                let addNotifications = await this.projectSrv.prepareNotificationModels();
                                for (let notification of addNotifications) {
                                    notification.type = DpNotificationType.ARTICLE_REMINDER;
                                    if (extensionModel.article.articleReminderName != '') {
                                        notification.name = extensionModel.article.articleReminderName;
                                    } else {
                                        notification.name = extensionModel.article.name;
                                    }
                                    notification.relatedJobExtension = extensionModel.pk;
                                    notification.relatedJobExtensionName = extensionModel.article.name;

                                    // calculate visibility for reminder
                                    let calculatedReminderDate = moment()
                                        .add(
                                            extensionModel.article.articleReminderDuration,
                                            extensionModel.article.articleReminderDurationType
                                        )
                                        .hour(0)
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0);

                                    if (!isNaN(calculatedReminderDate.valueOf())) {
                                        notification.notificationDate = calculatedReminderDate.valueOf();
                                    }
                                    this.job.notificationsToCreate.push(notification);
                                }
                            }
                        }
                        // override current jobextension form group with updated values from db
                        extensionFormGroup = this.utils.objectToFormGroup(extensionModel);

                        // check if we need to create calendarEntries for date fields
                        for (let field of extensionModel.article.fields) {
                            let dueDates = this.utils.getDueDatesForField({
                                field: field,
                                relatedProject: this.project,
                                relatedJob: this.job,
                                relatedJobExtension: extensionModel,
                                companyId: this.authSrv.authInfo.companyData._id,
                                userName: this.authSrv.authInfo.userData.userName,
                            });

                            for (let newDueDate of dueDates) {
                                let skipPush = false;

                                if (newDueDate.relatedFieldName) {
                                    for (let existingDueDate of this.job.dueDates) {
                                        if (existingDueDate.relatedFieldName !== newDueDate.relatedFieldName) continue;

                                        const sameExtension = existingDueDate.relatedJobExtension === newDueDate.relatedJobExtension;
                                        const sameTimestamp = existingDueDate.startTimestamp === newDueDate.startTimestamp;

                                        // Exact match (extension + field + timestamp) → already known, never recreate
                                        // (especially important when the previous entry was closed — don't restore it).
                                        if (sameExtension && sameTimestamp) {
                                            skipPush = true;
                                            continue;
                                        }

                                        // Newer entries can only close entries that are still OPEN.
                                        const isOpen = existingDueDate.doneAt == null || existingDueDate.doneAt === 0;
                                        if (!isOpen) continue;

                                        if (sameExtension) {
                                            // Same extension, different timestamp → older entry replaced.
                                            existingDueDate.doneAt = moment().valueOf();
                                            existingDueDate.doneBy = this.authSrv.authInfo.userData.userName;
                                            this.logger.log(
                                                `[JOB DETAILS] auto-closed older due date for same extension "${existingDueDate.relatedFieldName}" ` +
                                                    `(old: ${existingDueDate.startTimestamp}, new: ${newDueDate.startTimestamp})`
                                            );
                                        } else if (
                                            isNewExtension &&
                                            moment(existingDueDate.startTimestamp).isBefore(moment(newDueDate.startTimestamp))
                                        ) {
                                            // Different extension, older entry → replaced by newer extension.
                                            existingDueDate.doneAt = moment().valueOf();
                                            existingDueDate.doneBy = this.authSrv.authInfo.userData.userName;
                                            this.logger.log(
                                                `[JOB DETAILS] auto-closed older due date from other extension "${existingDueDate.relatedFieldName}" ` +
                                                    `(old: ${existingDueDate.startTimestamp}, new: ${newDueDate.startTimestamp})`
                                            );
                                        }
                                    }
                                }

                                if (!skipPush) {
                                    this.job.dueDates.push(newDueDate);
                                }
                            }

                            this.job.calculateNextDueDateTimestamp();
                        }
                    }
                }
            }

            // add created-job-extension infos to our job (we have to add it in reverse-order if our job-extensions are displayed descending (because the jobExtensions array IN our job is always ascending -> newest last)
            if (createdJobExtensionInfos.length > 0) {
                if (this.currentJobExtensionSortingMode === 'desc') {
                    createdJobExtensionInfos.reverse();
                }
                for (let jeInfo of createdJobExtensionInfos) {
                    this.job.jobExtensions.push(jeInfo);
                }
            }

            // handle saving of deleted job-extensions
            for (let jobExtensionId of this.jobExtensionIdsToDelete) {
                try {
                    await this.db.delete(jobExtensionId).toPromise();
                } catch (extensionDeleteErr) {
                    this.logger.error('[JOB DETAILS] failed to delete job extension in db', extensionDeleteErr);
                }
                // find the job extension info in our existing job and remove it from there
                let jobExtensionInfoInJobIndex = this.job.jobExtensions.findIndex((x) => x._id === jobExtensionId);
                this.logger.log(`[JOB DETAILS] existing job extension ${jobExtensionId} was removed from job ${this.job._id}`);
                if (jobExtensionInfoInJobIndex > -1) {
                    this.job.jobExtensions.splice(jobExtensionInfoInJobIndex, 1);
                }
            }

            // apply job-extension order from reorder
            this.job.jobExtensions = this.currentJobExtensionFormGroups
                .map((fg) => this.job.jobExtensions.find((je) => je._id === fg.value._id))
                .filter((je) => je != null);

            // as we have now updated all job-extension formgroups after saving them to db we need to re-initialize our change observables/subscriptions
            this.cleanupFormChangeSubscriptionsForJobExtensions();
            for (let extensionFormGroup of this.currentJobExtensionFormGroups) {
                this.setupFormChangeSubscriptionForJobExtension(extensionFormGroup);
            }

            // set job relation to current project
            this.job.relation = 'project#' + this.project._id; // set relation

            let jobNumberBeforeSave = this.job.jobNumber;

            // update job with form values
            this.job = new Job({ ...this.job, ...this.jobFormGroup.value });

            // check code-job-number-sync setting
            if (this.settingsSrv.getCompanySettings().syncJobNumberAndCode === true) {
                if (this.job.code != null && this.job.code != '') {
                    this.job.jobNumber = this.job.code;
                }
            }

            try {
                if (jobNumberBeforeSave !== this.job.jobNumber) {
                    for (let marker of this.job.marker) {
                        if (marker.markerName === jobNumberBeforeSave) {
                            marker.markerName = this.job.jobNumber;
                        }
                    }
                }
            } catch (jobNumberEditErr) {
                console.error('job number edit check failed', jobNumberEditErr);
            }

            // adjust job name based on articles
            if (this.settingsSrv.getCompanySettings().adjustJobNameBasedOnArticles === true) {
                // loop through job articles and job extensions, get the name of the first article and append count of articles
                let adjustedJobName = '';
                let articleCount = 0;

                if (this.job.articles.length > 0) {
                    let targetInd = this.currentJobExtensionSortingMode == 'desc' ? this.job.articles.length - 1 : 0;
                    adjustedJobName = this.job.articles[targetInd].name;
                    articleCount = this.job.articles.length;
                }

                // count number of existing job extension form groups (1 job extension = 1 article) - deleted job extensions are not included in this array, so already handled
                articleCount = articleCount + this.currentJobExtensionFormGroups.length - 1; // remove 1 from the count because we include the name of the first article in the job name

                // if our adjustedJobName is still empty (because the job has no deprecated old articles) get the article name of the first job extension
                if (adjustedJobName === '') {
                    if (this.currentJobExtensionFormGroups.length > 0) {
                        let targetInd = this.currentJobExtensionSortingMode == 'desc' ? this.currentJobExtensionFormGroups.length - 1 : 0;
                        adjustedJobName = this.currentJobExtensionFormGroups[targetInd].value.article.name;
                    }
                }

                if (adjustedJobName !== '') {
                    if (articleCount > 0) {
                        adjustedJobName = adjustedJobName + ' + ' + articleCount;
                    }
                }

                let adjust = false;
                if (this.job.name == this.serviceGroup.defaultJobName) {
                    adjust = true;
                } else if (this.job.name == this.job.lastAutomaticallyAdjustedName) {
                    // the name is still the auto adjusted name (so it was not changed manually since last adjustment), so we can adjust it
                    adjust = true;
                } else if (this.job.name == adjustedJobName) {
                    // the current form input name is exactly the same as the name we would now auto-adjust to, so we can handle it as auto-adjust and re-enable the auto-adjust (maybe it was disabled before)
                    adjust = true;
                }
                if (adjust) {
                    if (adjustedJobName !== '') {
                        this.job.name = adjustedJobName;
                    } else {
                        this.job.name = this.serviceGroup.defaultJobName;
                    }
                    this.job.lastAutomaticallyAdjustedName = this.job.name;
                }
            }

            // update job additional autofill fields if there are any (job extensions are handled in job-article-component)
            let currentSavedAutoFillFields = [];
            let savedAutoFillFieldData = await this.storage.get('dp_autofill_fields');
            if (Array.isArray(savedAutoFillFieldData) && savedAutoFillFieldData.length > 0) {
                currentSavedAutoFillFields = savedAutoFillFieldData;
            }

            for (let field of this.job.fields) {
                if (field.autoFillLastValue === true) {
                    // if a job-field has autofill activated, we add to saved auto fill fields array or replace the current saved value
                    let changedAutoFillFieldIndex = currentSavedAutoFillFields.findIndex((x) => x.fieldName === field.name);
                    if (changedAutoFillFieldIndex === -1) {
                        changedAutoFillFieldIndex = currentSavedAutoFillFields.length; // add to end of array
                    }
                    currentSavedAutoFillFields[changedAutoFillFieldIndex] = {
                        fieldName: field.name,
                        fieldValue: field.value,
                        timestamp: Date.now(),
                    };
                }
            }

            try {
                // remove the last locally saved autofill fields if they are older than 1 month - so that our locally saved fields dont get too large storage-wise
                currentSavedAutoFillFields = currentSavedAutoFillFields.filter((x) =>
                    moment(x.timestamp).isAfter(moment().subtract(30, 'days'))
                );
                await this.storage.set('dp_autofill_fields', currentSavedAutoFillFields); // update in local storage
            } catch (updateAutoFillFieldsErr) {
                this.logger.error('[JOB DETAILS] could not update auto fill fields in local storage');
            }

            this.refreshJobStatus();

            // update job linkings
            await this.updateJobLinkings();

            // clear referringSourceJobId on jobs whose link was removed from a linking-type field
            if (this.removedLinkedJobIdsFromFields.length > 0) {
                for (let removedJobId of this.removedLinkedJobIdsFromFields) {
                    try {
                        let removedJobData = new Job(await lastValueFrom(this.db.getItemById(removedJobId)));
                        if (removedJobData.referringSourceJobId === this.job._id) {
                            removedJobData.referringSourceJobId = null;
                            await this.db.put(removedJobData).toPromise();
                        }
                    } catch (err) {
                        this.logger.error('[JOB DETAILS] could not clear referringSourceJobId for removed linking field job', err);
                    }
                }
                this.removedLinkedJobIdsFromFields = [];
            }

            // loop through notifications which will be created and set related job info
            if (this.job.notificationsToCreate != null && this.job.notificationsToCreate.length > 0) {
                for (let notificationToCreate of this.job.notificationsToCreate) {
                    notificationToCreate.relatedJob = this.job.pk;
                    notificationToCreate.relatedJobName = this.job.name;
                    notificationToCreate.relatedJobNumber = this.job.jobNumber;
                    notificationToCreate.relatedProject = this.project.pk;
                    notificationToCreate.relatedProjectName = this.project.name;
                }
            }

            // save job to db
            let jobSaveRes = await this.db.put(this.job).toPromise();
            this.job = new Job(jobSaveRes);
            this.logger.log('[JOB DETAILS] job saved successfully', this.job);
            this.notify.success(this.translate.instant('projects.jobs.info.saved-job'));
            this.blockSave = false;
            this.jobWasSaved.emit({ job: this.job, closeJobModal: closeJobModal });

            if (closeJobModal) {
                if (await this.modalCtrl.getTop()) {
                    // Hopefully keep our app from remembering extensions on later job loads
                    this.currentJobExtensionFormGroups = [];

                    // close and destroy component
                    this.modalCtrl.dismiss({
                        state: JobModalDismissState.Save,
                        job: this.job,
                        closeJobModal: closeJobModal,
                    });
                }
            } else {
                this.initJobForm(closeJobModal);
            }
        } catch (saveErr) {
            this.blockSave = false;
            this.logger.error('error during job save process', saveErr);
            this.notify.error(this.translate.instant('projects.jobs.errors.save-job-err'));
            this.utils.sentryCaptureException(saveErr);
        }
    }

    /**
     * This function is called when saving the current job. It updates added or removed linkedJobs so that they also link to the currently updated job
     */
    updateJobLinkings() {
        return new Promise((resolve, reject) => {
            let errorOccured = null;

            // check if linkedJobs have changed, if so we will also update the other jobs, to show the relation to the current job
            let removedLinkedJobIds = this.initialLinkedJobIds.filter((x) => this.job.linkedJobIds.indexOf(x) < 0);
            let addedLinkedJobIds = this.job.linkedJobIds.filter((x) => this.initialLinkedJobIds.indexOf(x) < 0);

            // update removed linked jobs, so they dont link to this job anymore
            if (removedLinkedJobIds.length > 0) {
                for (let jobId of removedLinkedJobIds) {
                    this.db
                        .getItemById(jobId)
                        .pipe(
                            switchMap((job) => {
                                let jobModel = new Job(job);
                                let linkedJobIdIndex = jobModel.linkedJobIds.findIndex((x) => x == this.job._id);
                                if (linkedJobIdIndex > -1) {
                                    jobModel.linkedJobIds.splice(linkedJobIdIndex, 1);
                                }
                                return this.db.put(jobModel);
                            })
                        )
                        .subscribe(
                            (updateRes) => {
                                this.logger.log('[JOB DETAILS] updated linked job ' + jobId + ' to remove job linking', updateRes);
                            },
                            (updateErr) => {
                                this.logger.error(
                                    '[JOB LINKINGS] could not update linked job ' + jobId + ' to remove job linking',
                                    updateErr
                                );
                                this.utils.sentryCaptureException(updateErr);
                                errorOccured = updateErr;
                            }
                        );
                }
            }
            // update added linked jobs, so they also link to this job
            if (addedLinkedJobIds.length > 0) {
                for (let jobId of addedLinkedJobIds) {
                    this.db
                        .getItemById(jobId)
                        .pipe(
                            switchMap((job) => {
                                let jobModel = new Job(job);
                                if (Array.isArray(jobModel.linkedJobIds) && !jobModel.linkedJobIds.includes(this.job._id)) {
                                    jobModel.linkedJobIds.push(this.job._id);
                                    return this.db.put(jobModel);
                                } else {
                                    return of(true);
                                }
                            })
                        )
                        .subscribe(
                            (updateRes) => {
                                this.logger.log('[JOB LINKINGS] updated job ' + jobId + ' to add job linking', updateRes);
                            },
                            (updateErr) => {
                                this.logger.error('[JOB LINKINGS] could not update job ' + jobId + ' to add job linking', updateErr);
                                this.utils.sentryCaptureException(updateErr);
                                errorOccured = updateErr;
                            }
                        );
                }
            }

            if (errorOccured == null) {
                resolve(null);
            } else {
                reject(errorOccured);
            }
        });
    }

    /**
     * Triggerd when the user presses the delete button
     */
    async onDeleteJob() {
        this.notify
            .confirm(
                this.translate.instant('projects.jobs.info.confirm-delete-job-d-desc', {
                    jobNumber: this.job.jobNumber,
                    name: this.job.name,
                }),
                this.translate.instant('projects.jobs.info.confirm-delete-job-d-title'),
                this.translate.instant('shared.delete')
            )
            .then(async () => {
                this.projectSrv
                    .deleteJob(this.job)
                    .then(async (res) => {
                        this.logger.log('[JOB DETAILS] deleted job', res);
                        this.notify.success(this.translate.instant('projects.jobs.info.deleted-job'));

                        if (await this.modalCtrl.getTop()) {
                            await this.modalCtrl.dismiss({
                                state: JobModalDismissState.Delete,
                            });
                        } else {
                            // when showing job through modal we don't need to route from here cause it is already done in the calling component
                            this.router.navigate(['.'], { queryParams: {}, relativeTo: this.route });
                        }
                    })
                    .catch((err) => {
                        this.logger.log('[JOBS PAGE] could not delete job', err);
                    });
            })
            .catch(() => {
                // cancel button
            });
    }

    /**
     * Triggered when the user wants to duplicate the current job
     */
    onDuplicateJob() {
        this.projectSrv
            .duplicateJob(this.job)
            .then(async (data) => {
                this.logger.log('[JOB DETAILS] duplicated new job', data);
                this.router.navigate(['.'], { queryParams: { jobId: data._id }, relativeTo: this.route });

                if (await this.modalCtrl.getTop()) {
                    await this.modalCtrl.dismiss({
                        state: JobModalDismissState.Close,
                    });
                }
            })
            .catch((err) => {
                this.logger.error('[JOBS PAGE] could not clone new job', err);
                this.notify.error(this.translate.instant('projects.jobs.errors.duplicate-job-err'));
                this.utils.sentryCaptureException(err);
            });
    }

    /**
     * Changes job extension sorting direction
     * @param sorting
     */
    async changeJobExtSortingMode(sorting: 'desc' | 'asc') {
        if (this.currentJobExtensionSortingMode === sorting) {
            return;
        }
        await this.storage.set('dp_job_ext_sorting_mode', sorting);
        this.currentJobExtensionSortingMode = sorting;

        if (this.job.jobExtensions.length != this.currentJobExtensionFormGroups.length) {
            try {
                await this.notify.confirm(
                    this.translate.instant('core.data.unsaved-alert.message'),
                    this.translate.instant('core.data.unsaved-alert.title'),
                    this.translate.instant('core.data.unsaved-alert.leave-btn'),
                    this.translate.instant('core.data.unsaved-alert.stay-btn'),
                    true
                );

                this.initJobForm(true);
            } catch (cancelEx) {
                // user cancelled
            }
        } else {
            this.initJobForm(true);
        }
    }

    /**
     * Updates the visible status segments (job data, photos, building plan, qrcode) based on service group settings
     * @private
     */
    private updateSegmentVisibility() {
        if (this.serviceGroup) {
            this.hidePhotoSegment =
                (this.serviceGroup.settings.dataRecording.photoCategories.length == 0 && !this.serviceGroup.hasArticlesWithPhotos) ||
                (this.serviceGroup.hasArticlesWithPhotos &&
                    this.currentJobExtensionFormGroups.length == 0 &&
                    this.serviceGroup.settings.dataRecording.photoCategories.length == 0);
            this.hideCodeSegment = this.serviceGroup.settings.dataRecording.codeRecording == DataRecordingSetting.Disabled;
            this.hidePlanSegment =
                this.settingsSrv.currentUserRights.canSeeBuildingPlans === 'none' ||
                this.serviceGroup.settings.dataRecording.locationRecording == DataRecordingSetting.Disabled;
        } else {
            this.hidePhotoSegment = false;
            this.hideCodeSegment = false;
            this.hidePlanSegment = false;
        }
    }

    onDismiss() {
        this.modalCtrl.dismiss({
            state: JobModalDismissState.Close,
        });
    }

    async closeDialog() {
        if (await this.modalCtrl.getTop()) {
            this.modalCtrl.dismiss({
                state: JobModalDismissState.Close,
            });
        }
    }

    /**
     * Filters availableArticles and populates the filteredAvailableArticles when using the searchbar
     * @param filterValue
     */
    filterArticles(filterValue: string) {
        if (filterValue == '' || filterValue == undefined) {
            this.resetFilterArticles();
            return;
        }
        this.filteredAvailableArticles = this.availableArticles.filter(
            (i) => i.name.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1
        );
    }

    /**
     * Resets the filteredAvailableArticles ui search
     */
    resetFilterArticles() {
        this.filteredAvailableArticles = this.availableArticles;
        if (this.articleSearchBarCurrentValue !== '') {
            this.articleSearchBarCurrentValue = '';
        }
    }

    /**
     * Triggered when a job extension accordion is opened/closed, changes the current accordion status
     * @param data
     * @param self reference to the accordion-group to prevent false toggles from bubbling up change events
     */
    jobExtAccordionChanged(data, self) {
        if (self.el !== data.target) {
            return; // skip events that are not triggerd from the ionChange of the accordion-group itself, this could occur due to ionChange events form accordion content which bubble up (propagation)
        }

        this.handleAccordionToggle(data.detail.value as any[]);
    }

    /**
     * Checks if user can create a custom qr-code
     */
    checkCustomQRCodeGenerationAllowed() {
        let projectFound = false;
        if (
            Array.isArray(this.authSrv?.authInfo?.companyData?.companyFeatures?.customQRCodeGenerationProjects) &&
            this.authSrv?.authInfo?.companyData?.companyFeatures?.customQRCodeGenerationProjects?.includes(this.job.projectId)
        ) {
            projectFound = true;
        }

        this.canGenerateCustomQRCode = this.settingsSrv.currentCompanyFeatures.customQRCodeGeneration && !this.isViewer && projectFound;
    }

    /**
     * Triggered when user clicks on "generate qr-code" if module is active
     */
    generateQrCode() {
        if (!this.canGenerateCustomQRCode) {
            this.notify.error(this.translate.instant('projects.general.errors.action-err'));
            return;
        }
        if (!this.utils.isOnline) {
            this.notify.error(
                this.translate.instant('projects.general.errors.offline-err-desc'),
                this.translate.instant('projects.general.errors.offline-err-title')
            );
            return;
        }

        this.currentlyGeneratingQrCode = true;

        // now call the api (generate qr-code)
        this.http
            .post(environment.AWS_API_URL + '/qrcode/custom', {
                jobInfo: {
                    jobId: this.job?._id,
                    projectId: this.job?.projectId,
                    creatorCompanyId: this.job?.creatorCompanyId,
                    code: this.jobFormGroup?.get('code')?.value,
                    fields: this.jobFormGroup?.get('fields')?.value,
                },
            })
            .subscribe({
                next: async (res: any) => {
                    if (res?.qrCodeText != null && res.qrCodeText.trim() !== '') {
                        // only set code if it's not the same as before (to prevent triggering changeDetection)
                        let currentCode = this.jobFormGroup.get('code').value;
                        if (currentCode !== res.qrCodeText) {
                            this.jobFormGroup.get('code').setValue(res.qrCodeText);
                        }

                        await this.downloadQrCode(res.qrCodeFileData, res.qrCodeFileName);

                        this.notify.success(this.translate.instant('projects.jobs.info.generated-qrcode'));
                        this.currentlyGeneratingQrCode = false;
                    }
                },
                error: (err) => {
                    this.currentlyGeneratingQrCode = false;
                    this.logger.error('[JOBS PAGE] could not generate custom qrcode', err);
                    this.notify.error(this.translate.instant('projects.jobs.errors.generate-qrcode-err'));
                    this.utils.sentryCaptureException(err);
                },
            });
    }

    /**
     * Download Qr-Code on system
     */
    async downloadQrCode(fileData, fileName: string) {
        if (this.utils.isNative) {
            try {
                let result = await Filesystem.writeFile({
                    directory: Directory.Cache,
                    path: fileName,
                    data: fileData,
                });

                await Share.share({
                    title: fileName,
                    url: result.uri,
                    dialogTitle: this.translate.instant('projects.jobs.info.export-qrcode-title'),
                });
            } catch (ex) {
                if (
                    (typeof ex.errorMessage == 'string' && ex.errorMessage?.indexOf('Share canceled') != -1) ||
                    (typeof ex.message == 'string' && ex.message?.indexOf('Share canceled') != -1) ||
                    (typeof ex == 'string' && ex?.indexOf('Share canceled') != -1)
                ) {
                    this.logger.log('[JOBS PAGE] Share cancelled', ex);
                } else {
                    this.logger.error('[JOBS PAGE] Failed exporting qr-code file', ex);
                    this.notify.error(this.translate.instant('projects.jobs.errors.export-qrcode-err'));
                    this.utils.sentryCaptureException(ex);
                }
            }
        } else {
            let link = document.createElement('a');
            // download for html5 browsers
            if (link.download !== undefined) {
                link.setAttribute('href', fileData);
                link.setAttribute('download', fileName);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                this.notify.error(this.translate.instant('projects.jobs.errors.export-qrcode-err'));
                this.utils.sentryCaptureException(new Error('could not download qrcode'));
            }
        }
    }

    private handleAccordionToggle(openAccordionIds: any[]) {
        switch (this.serviceGroup.settings.dataRecording.serviceCollapse) {
            case DataCollapseSetting.Max5:
                if (openAccordionIds.length > 5) {
                    // remove the first item and keep the rest
                    this.currentExpandedJobExtensions = openAccordionIds.slice(1);
                } else {
                    this.currentExpandedJobExtensions = openAccordionIds;
                }
                break;
            case DataCollapseSetting.Max10:
                if (openAccordionIds.length > 10) {
                    // remove the first item and keep the rest
                    this.currentExpandedJobExtensions = openAccordionIds.slice(1);
                } else {
                    this.currentExpandedJobExtensions = openAccordionIds;
                }
                break;
            default:
                this.currentExpandedJobExtensions = openAccordionIds;
                break;
        }
    }

    /**
     * Open due date modal
     */
    openDueDateModal(): void {
        if (this.settingsSrv.getUserRights().canSeeCalendar !== 'none') {
            this.dueDateModalVisible = true;
        }
    }

    /**
     * Get all due dates they are not done yet
     */
    get dueDatesToDisplay(): DueDate[] {
        return this.job.dueDates?.filter((x) => x.doneAt == null || x.doneAt === 0);
    }

    /**
     * Get all due dates that are done
     */
    get doneDueDatesToDisplay(): DueDate[] {
        return this.job.dueDates?.filter((x) => x.doneAt != null && x.doneAt !== 0);
    }

    /**
     * Toggles the done state of a due date
     * @param dueDate the due date to toggle
     */
    onToggleDueDate(dueDate: DueDate): void {
        if (dueDate.doneAt != null && dueDate.doneAt !== 0) {
            delete dueDate.doneAt;
            delete dueDate.doneBy;
        } else {
            dueDate.doneAt = Date.now();
            dueDate.doneBy = this.authSrv.authInfo.userData.userName;
        }
        this.job.calculateNextDueDateTimestamp();
        this.dueDatesChanged = true;
    }

    /**
     * Checks if dueDate is done
     * @param doneAt a timestamp
     */
    isDueDateDone(doneAt: number): boolean {
        return doneAt != null && doneAt !== 0;
    }

    /**
     * On due date modal dismiss -> save the job if needed
     */
    async onDueDateModalDismiss(): Promise<void> {
        this.dueDateModalVisible = false;

        // only save if data is changed and the user has the rights
        if (this.dueDatesChanged && this.changesAllowed && this.settingsSrv.getUserRights().canManageCalendar) {
            this.dueDatesChanged = false;
            await this.onSaveJob(false);
        }
    }

    /**
     * start job-extensions reorder
     */
    onToggleReorder() {
        this.reorderExtensionsEnabled = !this.reorderExtensionsEnabled;
        if (this.reorderExtensionsEnabled) {
            this.expandedJobExtensionsBeforeReorder = [...this.currentExpandedJobExtensions];
            this.currentExpandedJobExtensions = [];
        } else {
            this.currentExpandedJobExtensions = [...this.expandedJobExtensionsBeforeReorder];
            this.expandedJobExtensionsBeforeReorder = [];
        }
    }

    /**
     * end job-extension reorder and save the new order
     * @param event
     */
    onReorderExtensions(event: CustomEvent) {
        const item = this.currentJobExtensionFormGroups.splice(event.detail.from, 1)[0];
        this.currentJobExtensionFormGroups.splice(event.detail.to, 0, item);

        event.detail.complete();
    }
}
